/**
 * API client with silent refresh-token rotation.
 *
 * - Attaches Authorization: Bearer <access> on every request.
 * - On 401, attempts ONE refresh via the shared singleton in
 *   [auth/refresh.ts] (same singleton used by bootRefresh) — prevents
 *   parallel refreshes from tripping the family-revoke-on-reuse defence.
 * - On refresh failure → caller surfaces the error; RequireAuth route
 *   guard bounces the user to /login.
 */
import { getAccessToken, getCsrfToken } from '../auth/tokenStore';
import { refreshAccessToken } from '../auth/refresh';

const API_BASE = import.meta.env.VITE_API_BASE ?? 'https://api.kidzio.io';

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface RequestOpts {
  method?: Method;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

export async function api<T = unknown>(path: string, opts: RequestOpts = {}): Promise<T> {
  const { method = 'GET', body, headers = {}, signal } = opts;
  // FormData bodies (multipart uploads, e.g. avatar) need to be sent
  // raw so the browser computes the boundary into Content-Type. Setting
  // Content-Type ourselves would corrupt the boundary and break parsing.
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;

  const send = async (): Promise<Response> => {
    const token = getAccessToken();
    const init: RequestInit = {
      method,
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        ...(body !== undefined && !isFormData ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(method !== 'GET' ? { 'X-CSRF-Token': getCsrfToken() ?? '' } : {}),
        ...headers,
      },
      body:
        body === undefined
          ? undefined
          : isFormData
            ? (body as FormData)
            : JSON.stringify(body),
      signal,
    };
    return fetch(`${API_BASE}${path}`, init);
  };

  let res = await send();
  if (res.status === 401 && path !== '/v1/auth/refresh') {
    if (await refreshAccessToken()) res = await send();
  }
  if (!res.ok) {
    const detail = await res.text().catch(() => res.statusText);
    throw new Error(`${res.status}: ${detail}`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * Like `api`, but returns the raw response body as a Blob — for binary
 * downloads (e.g. the monthly PDF report). Same Bearer + one-shot refresh.
 */
export async function apiBlob(path: string, opts: RequestOpts = {}): Promise<Blob> {
  const { method = 'GET', body, headers = {}, signal } = opts;
  const send = async (): Promise<Response> => {
    const token = getAccessToken();
    return fetch(`${API_BASE}${path}`, {
      method,
      credentials: 'include',
      headers: {
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(method !== 'GET' ? { 'X-CSRF-Token': getCsrfToken() ?? '' } : {}),
        ...headers,
      },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  };
  let res = await send();
  if (res.status === 401 && path !== '/v1/auth/refresh') {
    if (await refreshAccessToken()) res = await send();
  }
  if (!res.ok) {
    const detail = await res.text().catch(() => res.statusText);
    throw new Error(`${res.status}: ${detail}`);
  }
  return res.blob();
}
